import React, { useState } from 'react';
import { Filter, Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import OrderTableRow from './OrderTableRow';
import OrderMobileCard from './OrderMobileCard';
import FilePreview from '../common/FilePreview';
import { ORDER_STATUS, ORDER_STATUS_LABELS, ORDER_STATUS_COLORS } from '../../utils/constants';
import { formatOrderRef } from '../../utils/orderRef';

const OrdersTable = ({ orders, onOrderClick, onPatientClick, unreadCounts = {} }) => {
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [previewFile, setPreviewFile] = useState(null);

    // Fonction de tri
    const sortOrders = (ordersToSort, key, direction) => {
        return [...ordersToSort].sort((a, b) => {
            let aValue, bValue;

            switch (key) {
                case 'id':
                    aValue = a.id;
                    bValue = b.id;
                    break;
                case 'prosthesisType':
                    aValue = a.works?.[0]?.prosthesisType || '';
                    bValue = b.works?.[0]?.prosthesisType || '';
                    break;
                case 'orderDate':
                    aValue = new Date(a.orderDate);
                    bValue = new Date(b.orderDate);
                    break;
                case 'deliveryDate':
                    aValue = new Date(a.deliveryDate);
                    bValue = new Date(b.deliveryDate);
                    break;
                case 'status':
                    aValue = a.status;
                    bValue = b.status;
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    // Gérer le tri
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    // Gérer la prévisualisation
    const handlePreviewFile = (file) => {
        setPreviewFile(file);
    };

    const handleClosePreview = () => {
        setPreviewFile(null);
    };

    // Filtrer et trier les commandes
    const filteredOrders = orders.filter(order => {
        const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
        // Recherche dans les descriptions des travaux et le nom du patient
        const worksDescriptions = order.works?.map(w => w.description || '').join(' ') || '';
        const matchesSearch = searchTerm === '' ||
            worksDescriptions.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (order.patient_name_code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            order.id.toString().includes(searchTerm) ||
            formatOrderRef(order).toLowerCase().includes(searchTerm.toLowerCase());

        // Par défaut, ne pas afficher les commandes annulées sauf si le filtre est explicitement sur "Annulée"
        const isCancelled = order.status === ORDER_STATUS.CANCELLED;
        const showCancelled = statusFilter === ORDER_STATUS.CANCELLED;

        return matchesStatus && matchesSearch && (showCancelled || !isCancelled);
    });

    const sortedOrders = sortConfig.key
        ? sortOrders(filteredOrders, sortConfig.key, sortConfig.direction)
        : filteredOrders;

    // Compter les commandes par statut
    const statusCounts = orders.reduce((acc, order) => {
        acc[order.status] = (acc[order.status] || 0) + 1;
        return acc;
    }, {});

    // Composant pour les en-têtes triables
    const SortableHeader = ({ sortKey, children, className = "" }) => (
        <th
            className={`px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-white/10 transition-colors ${className}`}
            onClick={() => handleSort(sortKey)}
        >
            <div className="flex items-center space-x-1">
                <span>{children}</span>
                {sortConfig.key === sortKey ? (
                    sortConfig.direction === 'asc' ? (
                        <ArrowUp className="h-3 w-3" />
                    ) : (
                        <ArrowDown className="h-3 w-3" />
                    )
                ) : (
                    <ArrowUpDown className="h-3 w-3 opacity-50" />
                )}
            </div>
        </th>
    );

    return (
        <div className="space-y-6">
            {/* Filtres et recherche */}
            <div className="glass border border-white/10 p-6 rounded-lg shadow-sm">
                <div className="flex flex-col sm:flex-row gap-6">
                    {/* Barre de recherche */}
                    <div className="flex-1">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Rechercher par description ou numéro..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-black/30 border border-white/10 text-white placeholder-gray-500 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                            />
                        </div>
                    </div>

                    {/* Filtre par statut */}
                    <div className="flex items-center space-x-2">
                        <Filter className="h-4 w-4 text-gray-400" />
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="bg-black/30 border border-white/10 text-white rounded-md px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                        >
                            <option value="all">Tous les statuts ({orders.length})</option>
                            {Object.entries(ORDER_STATUS).map(([key, status]) => (
                                <option key={status} value={status}>
                                    {ORDER_STATUS_LABELS[status]} ({statusCounts[status] || 0})
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Indicateurs par statut : cliquables pour filtrer (remplit le filtre au-dessus) */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-4">
                {Object.values(ORDER_STATUS).map(status => {
                    const active = statusFilter === status;
                    return (
                        <button
                            key={status}
                            type="button"
                            onClick={() => setStatusFilter(active ? 'all' : status)}
                            className={`p-3 sm:p-4 rounded-lg border text-left transition-all ${ORDER_STATUS_COLORS[status]} ${active ? 'ring-2 ring-yellow-500' : 'opacity-80 hover:opacity-100'}`}
                        >
                            <div className="text-xl sm:text-2xl font-bold">{statusCounts[status] || 0}</div>
                            <div className="text-xs font-medium truncate">{ORDER_STATUS_LABELS[status]}</div>
                        </button>
                    );
                })}
            </div>

            {/* Commandes */}
            {sortedOrders.length === 0 ? (
                <div className="glass border border-white/10 rounded-lg shadow-sm overflow-hidden">
                    <div className="text-center py-12">
                        <div className="text-gray-400 text-lg mb-2">Aucune commande trouvée</div>
                        <div className="text-gray-500 text-sm">
                            {searchTerm || statusFilter !== 'all'
                                ? 'Essayez de modifier vos critères de recherche'
                                : 'Vous n\'avez pas encore passé de commande'
                            }
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {/* Table desktop */}
                    <div className="hidden md:block glass border border-white/10 rounded-lg shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-full">
                                <thead className="bg-black/30">
                                <tr>
                                    <th className="px-6 py-4 w-12"></th>
                                    <SortableHeader sortKey="id" className="w-20">N°</SortableHeader>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                                        Patient
                                    </th>
                                    <SortableHeader sortKey="prosthesisType" className="w-32">Type</SortableHeader>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider min-w-64">
                                        Description
                                    </th>
                                    <SortableHeader sortKey="orderDate" className="w-32">Passée le</SortableHeader>
                                    <SortableHeader sortKey="deliveryDate" className="w-32">Livraison</SortableHeader>
                                    <SortableHeader sortKey="status" className="w-32">Statut</SortableHeader>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-32">
                                        Actions
                                    </th>
                                </tr>
                                </thead>
                                <tbody className="glass divide-y divide-white/10">
                                {sortedOrders.map((order, index) => (
                                    <OrderTableRow
                                        key={order.id}
                                        order={order}
                                        onOrderClick={onOrderClick}
                                        onPreviewFile={handlePreviewFile}
                                        onPatientClick={onPatientClick}
                                        unreadCount={unreadCounts[order.id] || 0}
                                        index={index}
                                    />
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Cartes mobile */}
                    <div className="md:hidden space-y-3">
                        {sortedOrders.map((order, index) => (
                            <OrderMobileCard
                                key={order.id}
                                order={order}
                                onOrderClick={onOrderClick}
                                onPreviewFile={handlePreviewFile}
                                onPatientClick={onPatientClick}
                                unreadCount={unreadCounts[order.id] || 0}
                                index={index}
                            />
                        ))}
                    </div>
                </>
            )}

            {/* Informations de pagination */}
            {sortedOrders.length > 0 && (
                <div className="glass border border-white/10 px-4 py-3 rounded-lg">
                    <div className="text-sm text-gray-300">
                        Affichage de <span className="font-medium">{sortedOrders.length}</span> commande{sortedOrders.length > 1 ? 's' : ''}
                        {statusFilter !== 'all' || searchTerm ? (
                            <span> sur <span className="font-medium">{orders.length}</span> au total</span>
                        ) : null}
                    </div>
                </div>
            )}

            {/* Modal de prévisualisation */}
            {previewFile && (
                <FilePreview
                    file={previewFile}
                    onClose={handleClosePreview}
                />
            )}
        </div>
    );
};

export default OrdersTable;